import { ReceiptService } from './../../../revenue/shared/receipt/receipt.service';
import { CreditCardPaymentInfo } from './../../../revenue/shared/credit-card/credit-card-payment-info.model';
import { CreditCardGateway } from 'src/app/revenue/shared/credit-card-gateway/credit-card-gateway.model';
import { PaymentSource } from 'src/app/revenue/shared/payment-source/payment-source.model';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { PaymentHeaderDto } from './../../dto/payment-header.dto';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, SimpleChanges } from '@angular/core';
import { CreditCardGatewayService } from 'src/app/revenue/shared/credit-card-gateway/credit-card-gateway.service';
import { PrimeNgDropdownEvent } from 'src/app/shared/primeng/events/dropdown-event';
import { CreditCardInputComponent } from 'src/app/revenue/credit-card-input/credit-card-input.component';
import { RevenueObservationService } from 'src/app/revenue/shared/revenue-observation.service';
import { PrecznService } from 'src/app/integrations/preczn/shared/preczn.service';
import { TokenizeGatewayDto } from 'src/app/revenue/shared/gateway-resolution/dto/tokenize-gateway.dto';
@Component({
  selector: 'wl-invoice-payment-header',
  templateUrl: './invoice-payment-header.component.html',
  styleUrls: ['./invoice-payment-header.component.less'],
    standalone: false
})
export class InvoicePaymentHeaderComponent implements OnInit {
  @Input() paymentTypes: PaymentTypeDto[];
  @Input() paymentSources: PaymentSource[];
  @Input() creditCardPayment: CreditCardPaymentInfo;
  @Input() paymentHeader: PaymentHeaderDto;
  @Input() overpaying: boolean;
  @Input() paymentTypeInvalid: boolean;
  @Input() paymentTypeError: string;
  @Input() anyPaymentIsCreditCard: boolean = false;
  @Output() amountChanged = new EventEmitter<number>();
  @Output() paymentTypeSelected = new EventEmitter<void>();

  @ViewChild(CreditCardInputComponent) _creditCardInputComponent: CreditCardInputComponent;

  private _cardGateways: CreditCardGateway[];

  public sendAutomaticReceipt = false;
  // Direct-gateway surface: resolved from the paying CCGatewayID the selected payment type already
  // carries, not from revenue-item resolution.
  public resolvedGateway: TokenizeGatewayDto | null = null;
  private _cleanPaymentHeader: string;

  constructor(
    private _creditCardGatewayService: CreditCardGatewayService,
    private _receiptService: ReceiptService,
    private _revenueObservationService: RevenueObservationService,
    private _precznService: PrecznService
  ) {}

  ngOnInit() {
    this._creditCardGatewayService.getCreditCardGateways().then((data) => {
      this._cardGateways = data;
    });
    this._revenueObservationService.observeRequestInvoiceUpsert().subscribe(() => {
      this.setDirty();
    });
    this._revenueObservationService.observeInvoiceLoaded().subscribe(() => {
      this.setCleanObject();
    });
    this.setCleanObject();
  }

  get dirty() {
    return JSON.stringify(this.paymentHeader) != this._cleanPaymentHeader;
  }

  private setDirty() {
    this.paymentHeader.Dirty = this.dirty;
  }

  private setCleanObject() {
    this._cleanPaymentHeader = JSON.stringify(this.paymentHeader);
  }

  get showCreditCardInfo(): boolean {
    const isCreditCard = (this.selectedPaymentType && this.selectedPaymentType.CreditCardType) || this.anyPaymentIsCreditCard;
    return isCreditCard && this.paymentHeader.Amount > 0;
  }

  get selectedPaymentType() {
    return this.paymentHeader._paymentType;
  }
  set selectedPaymentType(value: PaymentTypeDto) {
    if (value) {
      this.paymentHeader._paymentType = value;
      this.paymentHeader.PaymentType = value.PaymentType;
      this.paymentHeader.PaymentTypeID = value.PaymentTypeId;
    }
  }

  get selectedPaymentSource() {
    return this.paymentHeader._paymentSource;
  }
  set selectedPaymentSource(value: PaymentSource) {
    if (value) {
      this.paymentHeader._paymentSource = value;
      this.paymentHeader.PaymentSource = value.PaymentSource;
    }
  }

  onAmountChange() {
    this.amountChanged.emit(this.paymentHeader.Amount);
  }

  paymentTypeChanged(data: PrimeNgDropdownEvent<PaymentTypeDto>) {
    this.updateCcGateway(data.value);
    this.updateUseCardConnect();
    this.paymentTypeSelected.emit();
  }

  onlyNumber(event: KeyboardEvent) {
    const value = (event.target as HTMLInputElement).value;
    const key = event.key;

    if (/^[0-9]$/.test(key)) return;
    if (key === '.') {
      if (value.indexOf('.') !== -1) {
        event.preventDefault();
        return;
      }
      return;
    }
    event.preventDefault();
  }

  private updateCcGateway(paymentType: PaymentTypeDto) {
    this.paymentTypes.forEach((type) => {
      if (type.PaymentTypeId == paymentType.PaymentTypeId) {
        this.creditCardPayment.CCGatewayID = type.CcGatewayId;
      }
    });
    this.updateResolvedGateway();
  }

  // The widget here is mounted unconditionally (it sits inside a [hidden] wrapper), so it loads the
  // SDK before any payment type has been picked; this is what retargets it once the gateway is
  // known.
  private updateResolvedGateway(): void {
    this._precznService.resolveTokenizeGateway(this.creditCardPayment?.CCGatewayID ?? 0).then((gateway) => {
      this.resolvedGateway = gateway;
    });
  }

  private updateUseCardConnect() {
    this._cardGateways.forEach((gateway) => {
      if (this.creditCardPayment.CCGatewayID == gateway.CCGatewayID) {
        if (gateway.GatewayTypeID == 6) {
          this._creditCardInputComponent.useCardConnect = true;
        } else {
          this._creditCardInputComponent.useCardConnect = false;
        }
        // GatewayTypeID 7 = Momentive Payments Authorize.Net (Preczn)
        // GatewayTypeID 8 = Momentive Payments CardPointe (Preczn)
        // On multi-gateway tenants isPrecznOnly() returns false, so usePreczn is never set by
        // CreditCardInputComponent.ngOnInit(). Enable/disable Preczn mode here based on the
        // selected payment type's gateway so the Preczn form and tokenization path activate
        // correctly regardless of how many gateways the tenant has configured.
        if (gateway.GatewayTypeID === 7 || gateway.GatewayTypeID === 8) {
          this._creditCardInputComponent.enablePrecznMode();
        } else {
          this._creditCardInputComponent.disablePrecznMode();
        }
      }
    });
  }

  public async prepareForSubmit(): Promise<void> {
    if (this._creditCardInputComponent?.usePreczn && this.showCreditCardInfo) {
      return await this._creditCardInputComponent.prepareForSubmit();
    }
  }
}
